import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check } from 'lucide-react';
import whyImage from '@/assets/why-pharmakademie-new.webp';

const benefits = [
  {
    title: 'Unabhängig',
    description: 'Wir finden für dich die besten Lösungen aus allen Angeboten, unabhängig und transparent. Du erfährst klar, wo die Unterschiede liegen.',
    highlight: 'Kein Produktverkauf, sondern individuelle Beratung!',
  },
  {
    title: 'Kostenfrei',
    description: 'Wir begleiten dich vom Studium, über das Berufsleben, bis zur Rente. Und das Beste daran:',
    highlight: 'Unsere Unterstützung ist für dich komplett kostenfrei!',
  },
  {
    title: 'Berufsspezifisch',
    description: 'Egal ob Apotheke, Industrie oder Forschung, dein Beruf bringt besondere Anforderungen mit sich. Wir kennen die Details in Klauseln und Bedingungen und sorgen dafür, dass du genau so aufgestellt bist, wie du es brauchst.',
    highlight: '',
  },
];

const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="services" className="relative py-20 md:py-28 bg-background" ref={ref}>
      <div className="container max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left Column - Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src={whyImage}
                alt="Pharmazeutische Beratung"
                className="w-full h-auto object-cover aspect-[4/3]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/10 via-transparent to-transparent" />
            </div>
          </motion.div>

          {/* Right Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Section Header */}
            <div className="mb-8">
              <p className="text-primary text-xs font-medium tracking-widest uppercase mb-3">
                Unsere Philosophie
              </p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-normal leading-[1.15] tracking-tight">
                Warum{' '}
                <span className="italic text-primary">Pharmakademie</span>?
              </h2>
            </div>

            {/* Benefits List */}
            <div className="space-y-5">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.4 + index * 0.1 }}
                  className="group"
                >
                  {/* Title with minimal check icon */}
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full border border-primary/30 flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-primary" strokeWidth={2.5} />
                    </span>
                    <h3 className="text-lg font-serif font-normal text-foreground tracking-tight">
                      {benefit.title}
                    </h3>
                  </div>
                  
                  {/* Description */}
                  <div className="pl-[26px]">
                    <p className="text-muted-foreground leading-relaxed text-sm">
                      {benefit.description}
                      {benefit.highlight && (
                        <span className="text-foreground font-medium ml-1">
                          {benefit.highlight}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Subtle divider (except last) */}
                  {index < benefits.length - 1 && (
                    <div className="pl-[26px] mt-5">
                      <div className="h-px bg-border/50 w-full" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
